import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * Minimum confirmation snapshot. The agent-bot orchestrator's
 * `confirmation_request` event carries a tool-use ID + prompt text;
 * the confirmation modal consumes this snapshot to render the
 * approve/reject UI and POST the user's choice to `/confirm`.
 */
export interface PendingConfirmation {
  toolUseID: string;
  prompt: string;
}

/**
 * Top-level body content of the chat panel.
 *
 *  - `chat` — landing hero or active conversation.
 *  - `history` — list of past sessions (`ChatHistoryView`).
 *
 * The kebab menu in the header toggles between these; closing the
 * panel doesn't change the view, so reopening returns the user to the
 * same place.
 */
export type ChatView = 'chat' | 'history';

interface ChatPanelState {
  isOpen: boolean;
  /**
   * Active feathers agent-sessions row id, or null when no session is
   * loaded (landing view). Persistence lives in feathers
   * `agent-sessions`; this provider only holds the currently bound id.
   */
  currentSessionID: string | null;
  /**
   * Routine the user has open in the editor, or null on non-routine
   * pages. Set from the routine-editor context.
   */
  currentRoutineID: string | null;
  /**
   * Latest pending tool-use confirmation, or null when none. Set from
   * the `/events` stream's `confirmation_request` payload and cleared
   * when the user resolves it.
   */
  pendingConfirmation: PendingConfirmation | null;
  /**
   * Which top-level body view the panel is showing. Defaults to
   * `'chat'`. Driven by the kebab menu's "Chat history" / "New chat"
   * items in `ChatHeader`.
   */
  chatView: ChatView;
}

interface ChatPanelActions {
  open: () => void;
  close: () => void;
  toggle: () => void;
  setCurrentSessionID: (sessionID: string | null) => void;
  setCurrentRoutineID: (routineID: string | null) => void;
  setPendingConfirmation: (confirmation: PendingConfirmation | null) => void;
  setChatView: (view: ChatView) => void;
  /**
   * Drop the current session, clear the pending confirmation, and
   * return to the `'chat'` view so the user lands on the hero.
   * Used by the kebab menu's "New chat" action.
   */
  startNewChat: () => void;
}

export type ChatPanelContextValue = ChatPanelState & ChatPanelActions;

const ChatPanelContext = createContext<ChatPanelContextValue | null>(null);

interface ChatPanelProviderProps {
  children: ReactNode;
  /** Test/dev seed. Defaults to a clean closed-panel state. */
  initialState?: Partial<ChatPanelState>;
}

const DEFAULT_STATE: ChatPanelState = {
  isOpen: false,
  currentSessionID: null,
  currentRoutineID: null,
  pendingConfirmation: null,
  chatView: 'chat',
};

/**
 * Holds ephemeral chat-panel UI state. Conversation history,
 * sessions, and feedback live in feathers `agent-*` services — this
 * provider only knows the open/closed state, the currently-bound
 * session/routine ids, and the latest pending tool-use confirmation.
 * Nothing here is persisted to localStorage.
 */
export function ChatPanelProvider({
  children,
  initialState,
}: ChatPanelProviderProps) {
  const [isOpen, setIsOpen] = useState(
    initialState?.isOpen ?? DEFAULT_STATE.isOpen,
  );

  const [currentSessionID, setCurrentSessionID] = useState<string | null>(
    initialState?.currentSessionID ?? DEFAULT_STATE.currentSessionID,
  );

  const [currentRoutineID, setCurrentRoutineID] = useState<string | null>(
    initialState?.currentRoutineID ?? DEFAULT_STATE.currentRoutineID,
  );

  const [pendingConfirmation, setPendingConfirmation] =
    useState<PendingConfirmation | null>(
      initialState?.pendingConfirmation ?? DEFAULT_STATE.pendingConfirmation,
    );

  const [chatView, setChatView] = useState<ChatView>(
    initialState?.chatView ?? DEFAULT_STATE.chatView,
  );

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  const startNewChat = useCallback(() => {
    setCurrentSessionID(null);
    setPendingConfirmation(null);
    setChatView('chat');
  }, []);

  const value = useMemo<ChatPanelContextValue>(
    () => ({
      isOpen,
      currentSessionID,
      currentRoutineID,
      pendingConfirmation,
      chatView,
      open,
      close,
      toggle,
      setCurrentSessionID,
      setCurrentRoutineID,
      setPendingConfirmation,
      setChatView,
      startNewChat,
    }),
    [
      isOpen,
      currentSessionID,
      currentRoutineID,
      pendingConfirmation,
      chatView,
      open,
      close,
      toggle,
      startNewChat,
    ],
  );

  return (
    <ChatPanelContext.Provider value={value}>
      {children}
    </ChatPanelContext.Provider>
  );
}

/**
 * Hook for components inside ChatPanelProvider. Throws if used
 * outside the provider — that's almost always a wiring bug.
 */
export function useChatPanel(): ChatPanelContextValue {
  const context = useContext(ChatPanelContext);

  if (context === null) {
    throw new Error('useChatPanel must be used inside a <ChatPanelProvider>');
  }

  return context;
}

/**
 * Outside the provider tree, callers can pull the context indirectly
 * via this safer hook that returns null instead of throwing. Useful
 * for launcher buttons that may exist before the provider mounts.
 */
export function useChatPanelOptional(): ChatPanelContextValue | null {
  return useContext(ChatPanelContext);
}
